"use client";

import { useMemo } from "react";
import { Check, GitCompare } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  addCompareProduct,
  removeCompareProduct,
  selectCompareProducts,
} from "@/Redux/slice/compareSlice";
import {
  getCompareProductKey,
  isSameCompareProduct,
  MAX_COMPARE_PRODUCTS,
  normalizeCompareProduct,
} from "@/utils/compareProducts";
import { cn } from "@/lib/utils";

const COMPARE_ADD_ANIMATION_EVENT = "alpha-md:compare-add-animation";
const COMPARE_REMOVE_ANIMATION_EVENT = "alpha-md:compare-remove-animation";

const CompareToggle = ({ product, compact = false, className = "" }) => {
  const dispatch = useDispatch();
  const products = useSelector(selectCompareProducts);
  const user = useSelector((state) => state?.userData?.userData?.user);
  const normalizedProduct = useMemo(
    () => normalizeCompareProduct(product),
    [product]
  );

  const canCompare =
    (user?.type === "physician" ||
      user?.type === "clinic" ||
      user?.type === "reps") &&
    Boolean(user?._id || user?.id);
  if (!canCompare || !normalizedProduct) return null;

  const selectedProduct = products.find(
    (selected) => isSameCompareProduct(selected, normalizedProduct)
  );
  const productKey = getCompareProductKey(selectedProduct || normalizedProduct);
  const isSelected = Boolean(selectedProduct);
  const isAtLimit = !isSelected && products.length >= MAX_COMPARE_PRODUCTS;
  const productName = normalizedProduct.brand || "this medicine";

  const handleToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isSelected) {
      window.dispatchEvent(
        new CustomEvent(COMPARE_REMOVE_ANIMATION_EVENT, {
          detail: { productKey },
        })
      );
      dispatch(removeCompareProduct(productKey));
      return;
    }

    if (isAtLimit) {
      toast.info(`You can compare up to ${MAX_COMPARE_PRODUCTS} medicines.`);
      return;
    }

    // Send only visual origin data. Redux is still updated immediately below;
    // the global tray uses this event for a lightweight decorative flight.
    const sourceButton = event.currentTarget;
    const origin = sourceButton.getBoundingClientRect();
    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!reduceMotion && typeof sourceButton.animate === "function") {
      try {
        sourceButton.animate(
          [
            { transform: "scale(1)", offset: 0 },
            { transform: "scale(0.9)", offset: 0.34 },
            { transform: "scale(1.045)", offset: 0.72 },
            { transform: "scale(1)", offset: 1 },
          ],
          {
            duration: 270,
            easing: "cubic-bezier(0.2, 0.8, 0.2, 1)",
          }
        );
      } catch {
        // A decorative animation must never block the Redux selection update.
      }
    }

    window.dispatchEvent(
      new CustomEvent(COMPARE_ADD_ANIMATION_EVENT, {
        detail: {
          origin: {
            x: origin.left + origin.width / 2,
            y: origin.top + origin.height / 2,
          },
          label: productName,
          nextCount: Math.min(products.length + 1, MAX_COMPARE_PRODUCTS),
          productKey,
        },
      })
    );
    dispatch(addCompareProduct(normalizedProduct));
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      aria-pressed={isSelected}
      aria-disabled={isAtLimit}
      aria-label={
        isSelected
          ? `Remove ${productName} from comparison`
          : isAtLimit
            ? `Comparison is full. Remove a medicine before adding ${productName}`
            : `Add ${productName} to comparison`
      }
      title={
        isAtLimit
          ? `Remove a medicine before adding another (maximum ${MAX_COMPARE_PRODUCTS})`
          : isSelected
            ? "In your comparison — click to remove"
            : undefined
      }
      className={cn(
        "inline-flex min-h-9 items-center justify-center gap-1.5 rounded-lg border px-3 py-1.5 font-poppins text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#354EF0] focus-visible:ring-offset-2",
        isSelected
          ? "border-[#354EF0] bg-[#EEF1FF] text-[#354EF0] hover:bg-[#E4E8FF]"
          : "border-[#D9D9D9] bg-white text-[#4B4B4B] hover:border-[#354EF0] hover:text-[#354EF0]",
        isAtLimit && "cursor-not-allowed opacity-60",
        compact && "!min-h-8 h-8 !px-2.5 !py-0 text-[12px]",
        className
      )}
    >
      {isSelected ? (
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <GitCompare className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {/* "Added" alone did not say added to what, and the button sits beside
          Favourite, so it read as ambiguous. Name the destination. */}
      <span>{isSelected ? "Added to compare" : "Compare"}</span>
    </button>
  );
};

export default CompareToggle;
